import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { WifiOffIcon } from 'lucide-react'
import { isNetworkError } from '@/lib/trpc'

/** Shows while the server cannot be reached, and reloads data once it answers again. */
export function ConnectionBanner(): React.JSX.Element | null {
  const queryClient = useQueryClient()
  const [lost, setLost] = useState(false)

  useEffect(() => {
    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return
      if (event.action.type === 'error' && isNetworkError(event.action.error)) setLost(true)
      if (event.action.type === 'success') setLost(false)
    })
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return
      if (event.action.type === 'error' && isNetworkError(event.action.error)) setLost(true)
    })
    const offline = (): void => setLost(true)
    window.addEventListener('offline', offline)
    return () => {
      unsubscribeQueries()
      unsubscribeMutations()
      window.removeEventListener('offline', offline)
    }
  }, [queryClient])

  useEffect(() => {
    if (!lost) return
    const timer = window.setInterval(async () => {
      try {
        if ((await fetch('/healthz', { cache: 'no-store' })).ok) {
          setLost(false)
          void queryClient.invalidateQueries()
        }
      } catch {
        // Still unreachable; try again on the next tick.
      }
    }, 5_000)
    return () => window.clearInterval(timer)
  }, [lost, queryClient])

  if (!lost) return null
  return (
    <div
      role="status"
      className="flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
    >
      <WifiOffIcon className="size-4 shrink-0" aria-hidden="true" />
      The connection to the server was lost. Changes can’t be saved until it is back; c-sheet keeps
      trying.
    </div>
  )
}
